import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router';
import { IonCard } from '@ionic/react';
import toast from 'react-hot-toast';
import GridHeader from '../../components/Manager/GridHeader/GridHeader';
import Grid from '../../components/Manager/Grid/Grid';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { scrollGridToTop } from '../../utils/managerUtils';
import { scrollGridToRowSmooth } from '../../utils/scrollGridToRowSmooth';
import { DataTableStateEvent } from 'primereact/datatable';
import useRoutes from '../../hooks/useRoutes';
import SubsetDataset from '../SubsetDataset/SubsetDataset';
import { useTranslate } from '../../hooks/context/useTranslate';
import { getSubsetAndItemParams } from '../../utils/getSubsetAndItemParams';
import { resolveRowExpandTarget } from '../../utils/resolveRowExpandTarget';
import {
  FullscreenReferenceType,
  GlobalLocationStateType,
  MatchMode,
  ReferenceControl,
} from '../../types/types';
import DatasetReference from '../Reference/DatasetReference';
import ComponentSectionContextProvider from '../../context/ComponentSectionContext';
import { useSelectedRowsContext } from '../../hooks/context/useSelectedRowsContext';
import { useScrollGridOnCachedQueryChange } from '../../hooks/useScrollGridOnCachedQueryChange';
import { getDataKey } from '../../utils/getDataKey';
import { getDatasetDataApiRequestData } from '../../api/utils/requestData/getDatasetDataApiRequestData';
import RequestDataProvider from '../../context/RequestDataContext';
import { useUserState } from '../../hooks/context/useUserState';
import { useDatasetUrlItemReloadGuard } from './hooks/useDatasetUrlItemReloadGuard';
import { useDatasetFilterFirstRedirectGuard } from './hooks/useDatasetFilterFirstRedirectGuard';
import { useInterDatasetGuard } from './hooks/useInterDatasetGuard';
import { useDatasetBackButtonGuard } from './hooks/useDatasetBackButtonGuard';
import { useDatasetExportNavigationGuard } from './hooks/useDatasetExportNavigationGuard';
import { useFilterFirstReturnFetchGuard } from './hooks/useFilterFirstReturnFetchGuard';
import { useDatasetInitialFetch } from './hooks/useDatasetInitialFetch';
import ResponseActionsScopeProvider from '../../context/ResponseActionsScopeContext';
import { GridColumn } from '../../components/Manager/Grid/types';
import { selectGridRows } from '../../utils/dataset/selectGridRows';
import { selectGridDataset } from '../../utils/dataset/selectGridDataset';
import { getControlWithResolvedFilter } from '../../utils/filters/getControlWithResolvedFilter';

const DatasetListPageWrapper = ({ children }: { children: ReactNode }) => (
  <div className="page-container">
    <IonCard>{children}</IonCard>
  </div>
);

const DatasetList: FC = () => {
  const [fullscreenReference, setFullscreenReference] =
    useState<FullscreenReferenceType | null>(null);
  const [datasetFirstProcessed, setDatasetFirstProcessed] =
    useState<boolean>(false);
  const [searchForRowInProcess, setSearchForRowInProcess] =
    useState<boolean>(false);

  const {
    cachedQueryIdRef,
    columnsWidth,
    data,
    dataset,
    datasetData,
    disabledLazyLoading,
    filter,
    gridHeaderHeight,
    isGridLoading,
    lazyState,
    referenceDatasets,
    setGridHeaderHeight,
    fetchDatasetData,
    reloadDatasetDefinition,
    loadMoreData,
    setLazyState,
    setReferenceDatasets,
    setHorizontalScroll,
    dataHookLoadingDatasetId,
    activeFilters,
    activeSorting,
    locateAndFetchById,
    slaveTabIndex,
    datasetControl,
  } = useDatasetContext();
  const {
    expandedRow,
    selectedRows,
    setExpandedRow,
    processSelectAllChange,
    processSelectedRowsChange,
    resetSelectedRows,
    selectAllChecked,
    selectedVirtualRows,
    reloadLocateScrollTargetRef,
    reloadLocateInProgress,
  } = useSelectedRowsContext();

  const {
    state: { token: sessionToken },
  } = useUserState();

  const routes = useRoutes();
  const history = useHistory();
  const { pathname, state } = useLocation<GlobalLocationStateType>();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { t } = useTranslate();

  const gridRef = useRef<HTMLDivElement | any>(null);
  const lookupAttemptedRef = useRef<boolean>(false);
  const dataKey = getDataKey(dataset);
  const gridData = useMemo(
    () => selectGridRows(data, dataset),
    [data, dataset]
  );
  const gridDataset = useMemo(() => selectGridDataset(dataset), [dataset]);
  const datasetShown = datasetFirstProcessed || datasetData?.lazy;
  const detailEnabled: boolean = dataset?.useDetail !== false;
  // No Detail tab and no subsets left to show: a row click only highlights.
  const rowExpansionDisabled: boolean =
    !detailEnabled && !dataset?.subsets?.length;

  /* GUARDS */
  useDatasetUrlItemReloadGuard();
  useInterDatasetGuard();
  useDatasetFilterFirstRedirectGuard(setDatasetFirstProcessed);
  useDatasetBackButtonGuard(datasetShown, gridRef);
  useDatasetExportNavigationGuard();
  useFilterFirstReturnFetchGuard(datasetShown, setSearchForRowInProcess);
  useDatasetInitialFetch({
    dataset,
    datasetId,
    data,
    datasetData,
    dataHookLoadingDatasetId,
    activeSorting,
    activeFilters,
    fetchDatasetData,
  });

  // Scroll to top when X-CACHEDQUERY-ID changes on lazy load
  useScrollGridOnCachedQueryChange(
    datasetData,
    datasetId,
    cachedQueryIdRef,
    gridRef
  );

  const requestData = useMemo(() => {
    if (!dataset) return undefined;

    return getDatasetDataApiRequestData(
      dataset,
      activeSorting,
      sessionToken,
      0,
      activeFilters || undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      datasetControl ?? undefined
    );
  }, [dataset, activeSorting, activeFilters, datasetControl, sessionToken]);

  const handleFetchDatasetData = (lazy = false, queryId?: string) => {
    if (!lazy) {
      scrollGridToTop(gridRef);
    }

    fetchDatasetData(lazy, queryId);
  };

  const handleReloadData = () => {
    const { subsetId, itemId } = getSubsetAndItemParams(pathname);
    scrollGridToTop(gridRef);
    fetchDatasetData(false);

    if (subsetId || itemId) {
      setExpandedRow(null);
      history.replace(routes.datasetList(datasetId));
    }
  };

  const handleOpenFilter = () => {
    history.push(routes.datasetDetailMasterFilter(datasetId), {
      fromPathname: pathname,
    });
  };

  // Fullscreen export keeps the current dataset context and returns via Back to list.
  const handleOpenExport = (exportMode: string) => {
    history.push(routes.datasetExport(datasetId, exportMode));
  };

  const handleToggleDetail = () => {
    const { subsetId } = getSubsetAndItemParams(pathname);

    // Checkbox-selected rows -> detail paginates over selectedRows only;
    // URL must point to one of them (not data[0]) or Detail's rowNotFound
    // fallback toast fires.
    if (selectedRows.length > 0) {
      const target = subsetId
        ? routes.datasetDetailSubset(
            datasetId,
            selectedRows[0][dataKey],
            subsetId
          )
        : routes.datasetDetail(datasetId, selectedRows[0][dataKey]);
      history.push(target);
      return;
    }

    if (expandedRow) {
      const target = subsetId
        ? routes.datasetDetailSubset(datasetId, expandedRow[dataKey], subsetId)
        : routes.datasetDetail(datasetId, expandedRow[dataKey]);
      history.push(target);
      return;
    }

    if (gridData && gridData.length > 0) {
      history.push(
        routes.datasetDetail(datasetId, String(gridData[0][dataKey]))
      );
      return;
    }

    toast(t('toasts.noData'));
  };

  const handleRowToggle = (row: any | null) => {
    // Row is closing, redirect to dataset list
    if (row === null) {
      history.push(routes.datasetList(dataset?.datasetId));
      return;
    }

    // Grid does not re-run this handler on row-reference change -> read URL live.
    const target = resolveRowExpandTarget(
      window.location.pathname,
      slaveTabIndex,
      dataset?.subsets,
      detailEnabled
    );

    if (target.kind === 'subset' && target.subsetId) {
      history.push(
        routes.subset(dataset?.datasetId, row[dataKey], target.subsetId)
      );
      return;
    }

    history.push(routes.subsetDetail(dataset?.datasetId, row[dataKey]));
  };

  const findRowCleanup = () => {
    history.replace(pathname, {
      ...state,
      fromForms: undefined,
      fetchDataOnFilterFirstDataset: undefined,
      forceFindInDataset:
        state.forceFindInDataset === true
          ? undefined
          : state.forceFindInDataset,
    });
  };

  const handleFindRowInDataset = useCallback(
    async (itemId: string, forceNewData: boolean = false) => {
      const resolvedData = data ? data : [];

      if (!forceNewData) {
        // 1) Search current data — if the row is here, expand + scroll, done.
        const foundIndex = resolvedData.findIndex(
          (row: any) => String(row[dataKey]) === itemId
        );

        if (foundIndex !== -1) {
          findRowCleanup();

          // Scroll first, then expand after the animation completes —
          // setting expandedRow mid-scroll re-renders rowExpansionTemplate
          // (SubsetDataset) and shifts the row out of the viewport.
          scrollGridToRowSmooth(gridRef, foundIndex, () => {
            setExpandedRow(resolvedData[foundIndex]);
            setSearchForRowInProcess(false);
          });
          return;
        }
      }

      // 2) Not in current page: run one probe/refetch. On success, updated `data`
      // reruns this effect and branch (1) expands the row. Ref guard prevents duplicates.
      if (lookupAttemptedRef.current) return;
      lookupAttemptedRef.current = true;

      if (
        state?.submitted !== undefined ||
        state?.fetchDataOnFilterFirstDataset !== undefined ||
        state?.forceNewData !== undefined
      ) {
        history.replace(pathname, {
          ...state,
          submitted: state.submitted === true ? undefined : state?.submitted,
          fetchDataOnFilterFirstDataset: state?.fetchDataOnFilterFirstDataset
            ? undefined
            : state?.fetchDataOnFilterFirstDataset,
          forceNewData: state?.forceNewData ? undefined : state?.forceNewData,
        });
      }

      await locateAndFetchById(
        itemId,
        forceNewData,
        () => {
          setExpandedRow(null);
          handleRowToggle(null);
          setSearchForRowInProcess(false);
        },
        () => {
          setSearchForRowInProcess(false);
          // Row is gone from the current filter — drop the dead /item URL
          history.replace(routes.datasetList(datasetId), {
            ...state,
            submitted: undefined,
            fromForms: undefined,
            forceFindInDataset: undefined,
            subsetItemId: undefined,
            subsetFromForms: undefined,
            subsetSubmitted: undefined,
            fetchDataOnFilterFirstDataset: undefined,
            forceNewData: undefined,
          });
        },
        // Fresh probe proved the row is gone — refetch so the grid drops it
        forceNewData
      );
    },
    [data, state]
  );

  const openSlaveFullscreenReference = (
    column: GridColumn,
    value: string | null,
    matchMode: MatchMode,
    label: string,
    datasetId: string,
    resolvedFilter?: Record<string, any> | null
  ) => {
    if (!column.filterItem) return;

    setFullscreenReference({
      name: column.name,
      datasetId,
      control: getControlWithResolvedFilter(
        column.filterItem.control as ReferenceControl,
        resolvedFilter
      ),
      value: value || '',
      label,
    });

    history.push(pathname + '/reference', {
      fromPathname: pathname,
      filterItemName: column.filterItem.name,
      matchMode,
      type: 'slave',
    });
  };

  const openMasterFullscreenReference = (
    column: GridColumn,
    value: string | null,
    matchMode: MatchMode,
    label: string,
    resolvedFilter?: Record<string, any> | null
  ) => {
    if (!column.filterItem) return;

    setFullscreenReference({
      name: column.name,
      datasetId: datasetId,
      control: getControlWithResolvedFilter(
        column.filterItem.control as ReferenceControl,
        resolvedFilter
      ),
      value: value || '',
      label,
    });

    history.push(pathname + '/reference', {
      fromPathname: pathname,
      filterItemName: column.filterItem.name,
      matchMode,
      type: 'master',
    });
  };

  useEffect(() => {
    lookupAttemptedRef.current = false;
  }, [pathname, activeFilters, activeSorting]);

  useEffect(() => {
    if (state?.isDatasetDefinitionReloadRequired !== true) return;

    history.replace(pathname, {
      ...state,
      isDatasetDefinitionReloadRequired: undefined,
    });
    reloadDatasetDefinition();
  }, [state?.isDatasetDefinitionReloadRequired]);

  useEffect(() => {
    scrollGridToTop(gridRef);
  }, [activeFilters, activeSorting]);

  // Handle finding row in dataset when returning from forms and when redirect between datasets
  useEffect(() => {
    const { itemId } = getSubsetAndItemParams(pathname);

    const baseOmitCondition =
      expandedRow ||
      !datasetShown ||
      !itemId ||
      dataHookLoadingDatasetId !== undefined;

    if (baseOmitCondition) return;

    if (state?.fetchDataOnFilterFirstDataset) {
      setSearchForRowInProcess(true);
      handleFindRowInDataset(itemId, true);
      return;
    }

    if (!data || isGridLoading) return;

    if (state?.forceFindInDataset === true) {
      setSearchForRowInProcess(true);
      handleFindRowInDataset(itemId, state?.forceNewData);
      return;
    }

    if (!state?.fromForms) return;

    // From forms flow
    setSearchForRowInProcess(true);
    handleFindRowInDataset(itemId, state?.submitted);
  }, [data, datasetShown]);

  useEffect(() => {
    if (pathname.endsWith('/reference') && !fullscreenReference) {
      history.replace(routes.datasetList(datasetId));
    }
  }, [fullscreenReference, pathname]);

  useEffect(() => {
    if (state?.triggerInitData === true) {
      handleReloadData();
    }
  }, []);

  const ReferencePage = (
    <DatasetListPageWrapper>
      <DatasetReference
        fullscreenReference={fullscreenReference}
        setFullscreenReference={setFullscreenReference}
      />
    </DatasetListPageWrapper>
  );

  const DatasetPage =
    datasetFirstProcessed || datasetData?.lazy ? (
      <DatasetListPageWrapper>
        <RequestDataProvider requestData={requestData}>
          <ComponentSectionContextProvider componentSection="list">
            <GridHeader
              toggleDetail={handleToggleDetail}
              openExport={handleOpenExport}
              refetchData={handleFetchDatasetData}
              openFilter={handleOpenFilter}
              setGridHeaderHeight={setGridHeaderHeight}
              disableDetail={!gridData || gridData.length === 0}
              reloadData={handleReloadData}
            />
          </ComponentSectionContextProvider>
          <Grid
            dataset={gridDataset}
            filter={filter}
            data={gridData}
            rowExpansionTemplate={(row) => (
              <ComponentSectionContextProvider componentSection="list-subset">
                <ResponseActionsScopeProvider scope="subset">
                  <SubsetDataset
                    row={row}
                    openFullscreenReference={openSlaveFullscreenReference}
                  />
                </ResponseActionsScopeProvider>
              </ComponentSectionContextProvider>
            )}
            disableRowExpansion={rowExpansionDisabled}
            dataLoading={
              isGridLoading || searchForRowInProcess || reloadLocateInProgress
            }
            selectedRows={selectedRows}
            lazyState={lazyState}
            expandedRow={expandedRow}
            dataKey={dataKey}
            gridRef={gridRef}
            disabledLazyLoading={
              disabledLazyLoading ||
              searchForRowInProcess ||
              reloadLocateInProgress
            }
            columnsWidth={columnsWidth}
            gridHeaderHeight={gridHeaderHeight}
            referenceDatasets={referenceDatasets}
            setLazyState={
              setLazyState as Dispatch<SetStateAction<DataTableStateEvent>>
            }
            processSelectAllChange={processSelectAllChange}
            processSelectedRowsChange={(checkboxEvent) =>
              processSelectedRowsChange(checkboxEvent, selectedVirtualRows)
            }
            resetSelectedRows={resetSelectedRows}
            onViewColumnsSaved={reloadDatasetDefinition}
            selectAllChecked={selectAllChecked}
            setExpandedRow={setExpandedRow}
            onHorizontalScroll={setHorizontalScroll}
            loadMoreRows={loadMoreData}
            setReferenceDatasets={setReferenceDatasets}
            onRowToggle={handleRowToggle}
            openFullscreenReference={openMasterFullscreenReference}
            scrollTargetOverrideRef={reloadLocateScrollTargetRef}
          />
        </RequestDataProvider>
      </DatasetListPageWrapper>
    ) : null;

  return (
    <Switch>
      <Route path={`/:rest(.+)/reference`}>{ReferencePage}</Route>
      <Route path={pathname} exact>
        {DatasetPage}
      </Route>
    </Switch>
  );
};

export default DatasetList;
